import * as THREE from "three";
import { G, Kit, col, hdr, instance, once, slab, spring, revolve, type V3 } from "./kit";
import { DISPLAY_FONT, canvasTex } from "./tex";

/**
 * Hover boards (one per board in meta/Shop.ts): a deck about 2.4 × 4.8 × 0.15 m that rides under the roadster. Top
 * surface at y = 0 (the car sits on it), centred on x/z, front = +z. Every board has a glowing LED skirt (mesh named
 * "underglow" — pulse it) and hover pads; each design has its own outline and details.
 */

const W = 2.4, L = 4.8, T = 0.15;

export const BOARD_COLORS: Record<string, string> = {
  default: "#9aa3ad", lowrider: "#e4393c", bouncer: "#f4c04e", drifter: "#3b7ddd", rocket: "#ff8a3c",
  double: "#8fd26b", zapper: "#9945ff", glider: "#7fd8c9", baltic: "#14f195", excel: "#1d7044"
};

/** Top-view outline helpers (x across, y = along the board, +y = front). */
function roundedOutline(w: number, l: number, rFront: number, rBack: number): THREE.Shape {
  const s = new THREE.Shape();
  const x = w / 2, y = l / 2;
  s.moveTo(-x + rBack, -y);
  s.lineTo(x - rBack, -y);
  s.quadraticCurveTo(x, -y, x, -y + rBack);
  s.lineTo(x, y - rFront);
  s.quadraticCurveTo(x, y, x - rFront, y);
  s.lineTo(-x + rFront, y);
  s.quadraticCurveTo(-x, y, -x, y - rFront);
  s.lineTo(-x, -y + rBack);
  s.quadraticCurveTo(-x, -y, -x + rBack, -y);
  return s;
}

function shapeFrom(points: [number, number][], curve: Set<number>): THREE.Shape {
  const s = new THREE.Shape();
  const n = points.length;
  const mid = (a: [number, number], b: [number, number]): [number, number] => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const start = mid(points[n - 1], points[0]);
  s.moveTo(...start);
  for (let i = 0; i < n; i++) {
    const p = points[i], m = mid(p, points[(i + 1) % n]);
    if (curve.has(i)) s.quadraticCurveTo(p[0], p[1], m[0], m[1]);
    else { s.lineTo(p[0], p[1]); s.lineTo(m[0], m[1]); }
  }
  return s;
}

function scaledShape(shape: THREE.Shape, sx: number, sy: number): THREE.Shape {
  const pts = shape.getPoints(10).map((p) => new THREE.Vector2(p.x * sx, p.y * sy));
  return new THREE.Shape(pts);
}

/** A deck slab from a top-view outline: top at y = `top`, `thick` deep, edges bevelled. */
function deck(shape: THREE.Shape, thick: number, bevel: number, top = 0): THREE.BufferGeometry {
  const g = slab(shape, thick, bevel, 10, 2);
  g.rotateX(Math.PI / 2);                       // outline y → +z (front), extrusion → −y
  g.translate(0, top - thick / 2, 0);
  return g;
}

/**
 * A flat shape facing up at height y. UVs are normalised over the board with u growing towards −x and v towards the
 * front, so texture text reads correctly from the chase camera behind.
 */
function topPlate(shape: THREE.Shape, y: number, w = W, l = L): THREE.BufferGeometry {
  const g = new THREE.ShapeGeometry(shape, 10);
  const pos = g.getAttribute("position");
  const uv = g.getAttribute("uv") as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) uv.setXY(i, -pos.getX(i) / w + 0.5, pos.getY(i) / l + 0.5);
  g.rotateX(-Math.PI / 2);                      // shape y → −z ...
  g.scale(1, 1, -1);                            // ... flipped back so +y → +z (front)
  g.translate(0, y, 0);
  // the mirror above flipped the winding; restore it so the plate faces up
  const index = g.getIndex();
  if (index) { const a = index.array as Uint16Array | Uint32Array; for (let i = 0; i < a.length; i += 3) { const t = a[i + 1]; a[i + 1] = a[i + 2]; a[i + 2] = t; } }
  g.computeVertexNormals();
  return g;
}

/** The LED skirt under the deck (named "underglow") + a soft light on the road (named "underlight"). */
function glowParts(shape: THREE.Shape, color: string, depth: number): THREE.Object3D[] {
  const led = new THREE.Mesh(deck(scaledShape(shape, 0.94, 0.95), 0.05, 0.0, -depth), once(`mat:boardled:${color}`, () => new THREE.MeshBasicMaterial({ color: hdr(color, 2.2), toneMapped: false })));
  led.name = "underglow";
  const lightTex = canvasTex("board-light", 128, 256, (ctx, w, h) => {
    const g = ctx.createRadialGradient(w / 2, h / 2, 4, w / 2, h / 2, h / 2);
    g.addColorStop(0, "rgba(255,255,255,0.9)"); g.addColorStop(0.5, "rgba(255,255,255,0.35)"); g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  });
  const light = new THREE.Mesh(G.plane(W * 1.5, L * 1.25), once(`mat:boardlight:${color}`, () => new THREE.MeshBasicMaterial({ map: lightTex, color: hdr(color, 1.0), transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false })));
  light.rotation.x = -Math.PI / 2;
  light.position.y = -depth - 0.4;
  light.name = "underlight";
  return [led, light];
}

/** Hover pads: a dark ring with a glowing core, under the deck. */
function pads(kit: Kit, color: string, at: [number, number][], y: number, r = 0.32): void {
  const ring = revolve([[r * 0.55, 0.02], [r, 0.0], [r, -0.06], [r * 0.7, -0.09]], 18, { axis: "y" });
  const core = revolve([[r * 0.72, -0.07], [0, -0.07]], 18, { axis: "y" });          // faces down
  for (const [x, z] of at) {
    kit.add(ring, "satin", 0x2a2c35, { p: [x, y, z] });
    kit.add(core, "glow", hdr(color, 2.0), { p: [x, y, z] });
  }
}

const gridTex = (): THREE.Texture => canvasTex("board-excel", 512, 1024, (ctx, w, h) => {
  ctx.fillStyle = "#1d7044";
  ctx.fillRect(0, 0, w, h);
  // header row (column letters) at the front (canvas top = front), row numbers down the left
  ctx.fillStyle = "#155836";
  ctx.fillRect(0, 0, w, 80);
  ctx.fillRect(0, 0, 70, h);
  ctx.strokeStyle = "rgba(255,255,255,0.55)";
  ctx.lineWidth = 3;
  for (let x = 70; x <= w; x += 110) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
  for (let y = 80; y <= h; y += 52) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
  ctx.fillStyle = "#e8fff2";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 46px ${DISPLAY_FONT}`;
  ["A", "B", "C", "D"].forEach((c, i) => ctx.fillText(c, 70 + 55 + i * 110, 42));
  ctx.font = `900 30px ${DISPLAY_FONT}`;
  for (let r = 0; r < 18; r++) ctx.fillText(String(r + 1), 35, 80 + 26 + r * 52);
  // the selected cell
  ctx.strokeStyle = "#14f195";
  ctx.lineWidth = 9;
  ctx.strokeRect(70 + 110 * 2, 80 + 52 * 15, 110, 52);
  ctx.fillStyle = "#14f195";
  ctx.fillRect(70 + 110 * 3 - 10, 80 + 52 * 16 - 10, 16, 16);
});

/** Grip tape: fine knurling (grey, tinted by the vertex colour). */
const gripTex = (): THREE.Texture => canvasTex("board-grip", 128, 128, (ctx, w, h) => {
  ctx.fillStyle = "#c8c8c8";
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 2;
  for (let i = -h; i < w; i += 8) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + h, h); ctx.stroke(); }
  ctx.strokeStyle = "rgba(0,0,0,0.25)";
  for (let i = 0; i < w + h; i += 8) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i - h, h); ctx.stroke(); }
}, { repeat: true });

const gripMaterial = (): THREE.MeshStandardMaterial => once("mat:board-grip", () => {
  const map = gripTex().clone();
  map.wrapS = map.wrapT = THREE.RepeatWrapping;
  map.repeat.set(10, 20);
  map.needsUpdate = true;
  return new THREE.MeshStandardMaterial({ map, vertexColors: true, roughness: 0.92, metalness: 0, envMapIntensity: 0.6 });
});

const stripeTex = (): THREE.Texture => canvasTex("board-lowrider", 256, 512, (ctx, w, h) => {
  ctx.fillStyle = "#e4393c";
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(w / 2 - 54, 0, 40, h);
  ctx.fillRect(w / 2 + 14, 0, 40, h);
  ctx.fillStyle = "#17181f";
  ctx.fillRect(0, 0, 10, h);
  ctx.fillRect(w - 10, 0, 10, h);
});

/** `buildHoverBoard(id)` → a Group, top at y = 0, centred, front = +z; children "underglow" + "underlight". */
export function buildHoverBoard(id: string): THREE.Group {
  const key = BOARD_COLORS[id] ? id : "default";
  return instance(`board:${key}`, () => {
    const color = BOARD_COLORS[key];
    const kit = new Kit();
    let outline = roundedOutline(W, L, 0.95, 0.6);
    let underDepth = T;
    // grip tape: a knurled dark shade of the board's own colour
    const gripColor = col(color).multiplyScalar(0.42).lerp(new THREE.Color(0x1a1b22), 0.35);
    const grip = (shape: THREE.Shape, c: number | string | THREE.Color = gripColor, s = 0.9) => kit.add(topPlate(scaledShape(shape, s, s + 0.04), 0.004), gripMaterial(), c);
    const padsAt: [number, number][] = [[-0.72, 1.55], [0.72, 1.55], [-0.72, -1.55], [0.72, -1.55]];

    switch (key) {
      case "lowrider": {
        outline = roundedOutline(2.5, L, 0.18, 0.18);
        kit.add(deck(outline, 0.12, 0.03), "gloss", color);
        kit.add(topPlate(scaledShape(outline, 0.97, 0.985), 0.004), once("mat:board-lowrider", () => new THREE.MeshStandardMaterial({ map: stripeTex(), roughness: 0.55, metalness: 0.05 })));
        for (const x of [-1.24, 1.24]) kit.add(G.box(0.05, 0.13, L - 0.36), "chrome", 0xdfe3ea, { p: [x, -0.06, 0] });
        underDepth = 0.12;
        pads(kit, color, padsAt, -0.12, 0.26);
        break;
      }
      case "bouncer": {
        outline = roundedOutline(2.3, 4.6, 0.85, 0.85);
        kit.add(deck(outline, T, 0.05), "gloss", color);
        grip(outline);
        for (const [x, z] of [[-0.78, 1.6], [0.78, 1.6], [-0.78, -1.6], [0.78, -1.6]] as [number, number][]) {
          kit.add(spring(0.17, 0.035, 3.5, 0.34, 10, 5), "chrome", 0xe8ecf2, { p: [x, -T - 0.34, z] });
          kit.add(G.cyl(0.22, 0.22, 0.05, 16), "satin", 0x2a2c35, { p: [x, -T - 0.37, z] });
          kit.add(G.cyl(0.14, 0.14, 0.02, 16), "glow", hdr(color, 2.0), { p: [x, -T - 0.4, z] });
        }
        underDepth = T;
        break;
      }
      case "drifter": {
        outline = shapeFrom([[0, 2.4], [0.95, 2.15], [1.2, 0.6], [1.22, -1.0], [1.45, -2.45], [0.7, -1.95], [0, -2.1], [-0.7, -1.95], [-1.45, -2.45], [-1.22, -1.0], [-1.2, 0.6], [-0.95, 2.15]], new Set([0, 1, 2, 6, 10, 11]));
        kit.add(deck(outline, T, 0.05), "gloss", color);
        grip(outline, gripColor, 0.88);
        // white swept stripes towards the wing tips
        for (const s of [-1, 1]) {
          const fin = shapeFrom([[s * 0.95, 0.4], [s * 1.12, 0.4], [s * 1.36, -2.25], [s * 1.2, -2.25]], new Set());
          kit.add(topPlate(fin, 0.008), "paint", 0xffffff);
          kit.add(G.box(0.04, 0.3, 0.9), "gloss", 0xffffff, { p: [s * 1.2, 0.14, -1.6], r: [0, -s * 0.08, 0] });
        }
        pads(kit, color, padsAt, -T);
        break;
      }
      case "rocket": {
        outline = shapeFrom([[0, 2.4], [0.75, 1.55], [1.12, 0.5], [1.12, -2.3], [-1.12, -2.3], [-1.12, 0.5], [-0.75, 1.55]], new Set([0, 1, 6]));
        kit.add(deck(outline, T, 0.05), "gloss", color);
        grip(outline, gripColor, 0.86);
        kit.add(topPlate(shapeFrom([[0, 2.3], [0.42, 1.7], [-0.42, 1.7]], new Set()), 0.008), "paint", 0xffffff);
        for (const x of [-0.55, 0.55]) {
          const outside = revolve([[0.13, 0], [0.18, -0.12], [0.3, -0.5]], 16, { smooth: true });
          const inside = revolve([[0.285, -0.5], [0.17, -0.13], [0.12, -0.02]], 16, { smooth: true });
          kit.add(G.cyl(0.2, 0.2, 0.3, 16), "satin", 0x3a3d48, { p: [x, -0.08, -2.35], r: [Math.PI / 2, 0, 0] });
          kit.add(outside, "metal", 0xc9ced8, { p: [x, -0.08, -2.48] });
          kit.add(inside, "satin", 0x3a2a24, { p: [x, -0.08, -2.48] });
          kit.add(revolve([[0.25, -0.42], [0, -0.06]], 16), "glow", hdr("#ffb347", 2.2), { p: [x, -0.08, -2.48] });
        }
        for (const s of [-1, 1]) kit.add(G.box(0.05, 0.4, 0.8), "gloss", 0xffffff, { p: [s * 1.1, 0.16, -1.8] });
        pads(kit, color, padsAt, -T);
        break;
      }
      case "double": {
        outline = roundedOutline(2.3, 4.6, 0.6, 0.6);
        kit.add(deck(outline, 0.12, 0.04), "gloss", color);
        grip(outline);
        kit.add(deck(scaledShape(outline, 0.92, 0.94), 0.12, 0.04, -0.42), "gloss", col(color).multiplyScalar(0.75));
        for (const [x, z] of [[-0.85, 1.7], [0.85, 1.7], [-0.85, 0], [0.85, 0], [-0.85, -1.7], [0.85, -1.7]] as [number, number][]) kit.add(G.cyl(0.07, 0.07, 0.32, 10), "chrome", 0xe3e7ee, { p: [x, -0.27, z] });
        underDepth = 0.54;
        pads(kit, color, padsAt, -0.54, 0.28);
        break;
      }
      case "zapper": {
        outline = roundedOutline(2.4, 4.8, 0.6, 0.6);
        kit.add(deck(outline, T, 0.05), "gloss", color);
        grip(outline);
        // lightning arcs along both sides and across the nose
        const bolt: V3[] = [[0, 0, 2.0], [0, 0.05, 1.4], [0, -0.04, 1.0], [0, 0.06, 0.3], [0, -0.05, -0.3], [0, 0.05, -0.9], [0, -0.04, -1.5], [0, 0.03, -2.0]];
        for (const s of [-1, 1]) {
          for (let i = 0; i < bolt.length - 1; i++) {
            const a = bolt[i], b = bolt[i + 1];
            const dy = b[1] - a[1], dz = b[2] - a[2];
            kit.add(G.box(0.025, 0.025, Math.hypot(dy, dz) + 0.02), "glow", hdr("#8ff8ff", 2.2), { p: [s * 1.205, -0.075 + (a[1] + b[1]) / 2, (a[2] + b[2]) / 2], r: [-Math.atan2(dy, dz), 0, 0] });
          }
          for (const z of [1.9, -1.9]) kit.add(G.sphere(0.09, 12, 8), "chrome", 0xe8ecf2, { p: [s * 0.95, 0.06, z] });
        }
        const nose: [number, number][] = [[-0.8, 2.05], [-0.45, 2.2], [-0.15, 2.0], [0.2, 2.22], [0.5, 2.02], [0.8, 2.15]];
        for (let i = 0; i < nose.length - 1; i++) {
          const [x0, z0] = nose[i], [x1, z1] = nose[i + 1];
          kit.add(G.box(Math.hypot(x1 - x0, z1 - z0) + 0.02, 0.02, 0.04), "glow", hdr("#8ff8ff", 2.2), { p: [(x0 + x1) / 2, 0.012, (z0 + z1) / 2], r: [0, -Math.atan2(z1 - z0, x1 - x0), 0] });
        }
        pads(kit, color, padsAt, -T);
        break;
      }
      case "glider": {
        outline = roundedOutline(2.0, 4.8, 0.8, 0.5);
        kit.add(deck(outline, T, 0.05), "gloss", color);
        grip(outline);
        for (const s of [-1, 1]) {
          const finShape = shapeFrom([[0, 1.0], [0.95, -0.35], [0.95, -1.35], [0, -1.45]], new Set([1]));
          const fin = deck(finShape, 0.05, 0.015, 0);
          kit.add(fin, "gloss", color, { p: [s * 0.92, -0.06, 0], r: [0, 0, s * -0.14], s: [s, 1, 1] });
          kit.add(G.box(0.05, 0.05, 1.02), "paint", 0xffffff, { p: [s * 1.86, -0.18, -0.85] });
          kit.add(G.box(0.06, 0.08, 1.3), "satin", 0x2a2c35, { p: [s * 0.98, -0.05, -0.2] });
        }
        pads(kit, color, padsAt.map(([x, z]) => [x * 0.85, z] as [number, number]), -T);
        break;
      }
      case "baltic": {
        outline = roundedOutline(2.4, 4.8, 0.5, 0.5);
        kit.add(deck(outline, T, 0.05), "gloss", color);
        grip(outline);
        // the three flags along each rail: LT (front), LV, EE (back), each a tricolour stacked on the rail's side
        const flags: [string, string, string][] = [["#fdb913", "#006a44", "#c1272d"], ["#9e3039", "#ffffff", "#9e3039"], ["#0072ce", "#111111", "#ffffff"]];
        for (const s of [-1, 1]) {
          flags.forEach((f, i) => {
            const zc = 1.25 - i * 1.25;
            f.forEach((c, k) => kit.add(G.box(0.07, 0.05, 1.2), "paint", c, { p: [s * 1.215, -0.025 - k * 0.05, zc] }));
          });
        }
        pads(kit, color, padsAt, -T);
        break;
      }
      case "excel": {
        outline = roundedOutline(2.4, 4.8, 0.2, 0.2);
        kit.add(deck(outline, T, 0.04), "gloss", 0x155836);
        kit.add(topPlate(scaledShape(outline, 0.97, 0.985), 0.004), once("mat:board-excel", () => new THREE.MeshStandardMaterial({ map: gridTex(), roughness: 0.78, metalness: 0 })));
        for (const s of [-1, 1]) kit.add(G.box(0.04, 0.1, L - 0.5), "paint", 0x14f195, { p: [s * 1.21, -0.07, 0] });
        pads(kit, "#14f195", padsAt, -T);
        break;
      }
      default: {
        kit.add(deck(outline, T, 0.05), "gloss", color);
        grip(outline);
        kit.add(topPlate(roundedOutline(0.26, 4.1, 0.13, 0.13), 0.008), "paint", 0xffffff);
        pads(kit, "#7fe9ff", padsAt, -T);
      }
    }
    const root = new THREE.Group();
    root.name = `board-${key}`;
    root.userData.anim = "board";
    root.add(kit.build({ name: "deck" }), ...glowParts(outline, key === "excel" ? "#14f195" : key === "default" ? "#7fe9ff" : color, underDepth));
    return root;
  });
}
